/**
 * THREE.js Wrapper as a React Component
 * @module components/ThreeScene
 */
import React from 'react';
import { withRouter } from 'react-router-dom';
import Stats from 'stats-js';
import * as THREE from 'three';
import { VRButton } from 'three/examples/jsm/webxr/VRButton.js';
import PubSub from 'pubsub-js';

import Item from './Item';
import Tooltip from './Tooltip';
import { MOUSE_CLICK, LOAD_START, LOAD_END } from '../events.js';
    
import ViewRandom from '../views/ViewRandom';
import ViewArtistList from '../views/ViewArtistList';
import ViewColor from '../views/ViewColor';
import ViewTime from '../views/ViewTime';
import Debug from './Debug';
import Camera from './Camera';
import Navigator from './Navigator';


const availableViews = [ViewRandom, ViewColor, ViewTime, ViewArtistList];

class ThreeScene extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            items: [],
            //boxhelper: [],
            hoverItem: null,
            view: null
        };

        this.cam = null;
        this.vrButton = null;

        this.mount = React.createRef();
        this.tooltip = React.createRef();

        //this.stats = new Stats();
    }

    componentDidMount() {

        /*document.body.appendChild(this.stats.dom);
        this.stats.dom.style.left = 'auto';
        this.stats.dom.style.right = '0px';*/

        window.addEventListener('resize', this.handleResize);

        const view = availableViews.find(v => v.path === this.props.location.pathname) || ViewRandom;
        this.setState({view}, () => {
            this.cam && this.cam.setView(view);
        });

        PubSub.subscribe(MOUSE_CLICK, this.handleMouseClick);
        PubSub.publish(LOAD_START, true);

        this.buildScene();
        this.createItems();
    }

    componentDidUpdate(prevProps) {
        if(this.props.location !== prevProps.location) {
          this.onRouteChanged();
        }
    }

    onRouteChanged() {

        let view = null;

        switch(this.props.location.pathname) {

            case '/color/':
                view = ViewColor;
                break;
            case '/time/':
                view = ViewTime;
                break;
            case '/artist-list/':
                view = ViewArtistList;
                break;
            case '/':
                view = ViewRandom;
                break;
        }

        if(view && view !== this.state.view) {
            
            this.state.view.willUnmount(this.state.items);
            view.willMount();
            this.cam.setView(view);
            
            this.setState({ view });
            view.arrangeItems(this.state.items);
        }
    }

    buildScene() {

        this.scene = new THREE.Scene();
        this.raycaster = new THREE.Raycaster();

        //CAMERA
        this.cam = new Camera({ scene: this.scene });
        this.camera = this.cam.camera;

        //RENDERER
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.renderer.setClearColor(0x242424);
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.xr.enabled = true;
        this.mount.current.appendChild(this.renderer.domElement);

        //FOG
        const fogColor = new THREE.Color(0x242424);
        this.scene.background = fogColor;
        this.scene.fog = new THREE.Fog(fogColor, 40, 80);

        //VR BUTTON
        if('xr' in navigator) {
            navigator.xr.isSessionSupported('immersive-vr').then(supported => {
				if(!supported) return;
                this.vrButton = VRButton.createButton(this.renderer);
                document.body.appendChild(this.vrButton);
			});
        }
        
    }

    createItems = () => {
        const items = [];
        //const boxhelper = [];
        const promises = [];

        this.props.data.forEach((row, i) => {
            const item = new Item({...row, cam: this.cam});
            items.push(item);

            const p = item.load();
            promises.push(p);
        });

        Promise.all(promises).then((elements) => {
            this.scene.add(...elements);
            console.log('added all items to scene');
            
            this.setState({items});
            this.start();

            //hotfix: prevent the scene not being fully loaded before arrangeItems
            window.setTimeout(() => {
                items.forEach(item => item.desiredOpacity = 1);
                this.state.view.arrangeItems(items, true);
            }, 500);
    
            PubSub.publish(LOAD_END, true);
        })


        
    }

    start = () => { this.renderer.setAnimationLoop(this.update); }
    stop = () => { this.renderer.setAnimationLoop(null); }
    componentWillUnmount() {
        this.stop();
        window.removeEventListener('resize', this.handleResize);
        this.mount.current.removeChild(this.renderer.domElement);
        this.vrButton && document.body.removeChild(this.vrButton);
        this.setState({items: []});
    }
    handleResize = (e) => {
        this.cam.handleResize(e);
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }

    update = () => {
        //this.stats.begin();
        this.state.view.update(this.cam, this.state.items);

        this.cam.update();
        this.updateHover();

        for(let item of this.state.items) item.update();
        //for(let item of this.state.boxhelper) item.update();

        this.renderer.render(this.scene, this.camera);
        //this.stats.end();
    }

    updateHover = () => {

        const x = ( this.props.mousePosition.x / window.innerWidth ) * 2 - 1;
        const y = -( this.props.mousePosition.y / window.innerHeight ) * 2 + 1;
        const mouse3 = new THREE.Vector2(x, y);

        if(this.tooltip.current) this.tooltip.current.update();

        this.raycaster.setFromCamera(mouse3, this.camera);
        const intersects = this.raycaster.intersectObjects(this.scene.children);
        if(intersects.length > 0 ) {
            const hoverItem = intersects[0].object.item;
            if(hoverItem !== this.state.hoverItem) {
                this.setState({hoverItem});
            }
        } else {
            this.setState({hoverItem: null});
        }
    }

    handleMouseClick = (msg, data) => {

        if(this.state.hoverItem) {
            this.props.history.push(`/detail/${this.state.hoverItem.props.workID}${this.state.view.path}`);
        }
    }

    render() {
        return (
            <div className="canvasWrap" ref={this.mount}>
                {/*<Debug cam={this.cam} />*/}
                <Tooltip ref={this.tooltip} item={this.state.hoverItem} />
                <Navigator cam={this.cam} view={this.state.view} />
            </div>
        );
    }
}

export default withRouter(ThreeScene)